/**
 * Copyright 2026 Ziz Academy
 */

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { examApi, imageApi, categoryApi, questionApi, examFolderApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Question, Answer, QuestionType, ExamCategory } from '../types';
import QuestionEditor from '../components/QuestionEditor';

const CreateExam: React.FC = () => {
    const selectionRef = useRef<Range | null>(null);
    const saveSelection = () => {
        const sel = window.getSelection();
        if (sel && sel.rangeCount > 0) selectionRef.current = sel.getRangeAt(0);
    };
    const restoreSelection = () => {
        const sel = window.getSelection();
        if (selectionRef.current && sel) {
            sel.removeAllRanges();
            sel.addRange(selectionRef.current);
        }
    };
    const [title, setTitle] = useState('');
    // Rich editor refs per question (for inserting tables and toggling borders)
    const editorRefs = useRef<(HTMLDivElement | null)[]>([]);
    const setEditorRef = (index: number, el: HTMLDivElement | null) => {
        editorRefs.current[index] = el;
    };
    const [description, setDescription] = useState('');
    const [durationMinutes, setDurationMinutes] = useState(30);
    const [questions, setQuestions] = useState<Question[]>([]);
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    const [categories, setCategories] = useState<ExamCategory[]>([]);
    const [categoryId, setCategoryId] = useState<number | undefined>(undefined);
    const [newCategoryName, setNewCategoryName] = useState<string>('');
    // Class/Grade selection (local only)
    const defaultClasses = ['Open','1st Class','2nd Class','3rd Class','4th Class','5th Class','6th Class','7th Class','8th Class','9th Class','10th Class','11th Class','12th Class'];
    const [classes, setClasses] = useState<string[]>(defaultClasses);
    const [classLevel, setClassLevel] = useState<string>('Open');
    const [newClassName, setNewClassName] = useState<string>('');

    // Select-from-bank modal state
    const [selectOpen, setSelectOpen] = useState(false);
    const [pickSearch, setPickSearch] = useState('');
    const [pickSubject, setPickSubject] = useState('');
    const [pickClass, setPickClass] = useState('');
    const [pickPage, setPickPage] = useState(0);
    const [pickSize] = useState(10);
    const [pickTotalPages, setPickTotalPages] = useState(0);
    const [pickTotal, setPickTotal] = useState(0);
    const [pickList, setPickList] = useState<Question[]>([]);
    const [pickLoading, setPickLoading] = useState(false);
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const [importing, setImporting] = useState(false);

    const { user } = useAuth();
    const navigate = useNavigate();

    // Folder selection state and options
    const [folderOptions, setFolderOptions] = useState<{ id: number | null; label: string }[]>([]);
    const [selectedFolderId, setSelectedFolderId] = useState<number | null>(null);

    useEffect(() => {
        const load = async () => {
            try {
                const list = await categoryApi.list();
                setCategories(list);
                // Auto-select 'misc' if present
                const misc = list.find(c => c.name.toLowerCase() === 'misc');
                if (misc) setCategoryId(misc.id);
            } catch (e) {
                console.error('Failed to load categories', e);
            }
        };
        load();
    }, []);

    // Load folder tree for acting user and flatten to options
    useEffect(() => {
        const loadFolders = async () => {
            try {
                if (!user?.id) { setFolderOptions([{ id: null, label: '— None —' }]); return; }
                const tree = await examFolderApi.getTree(user.id);
                const flatten = (nodes: any[], level = 0): { id: number | null; label: string }[] => {
                    const res: { id: number | null; label: string }[] = [];
                    for (const n of nodes || []) {
                        res.push({ id: n.id, label: `${'— '.repeat(level)}${n.name}` });
                        if (n.children && n.children.length > 0) {
                            res.push(...flatten(n.children, level + 1));
                        }
                    }
                    return res;
                };
                const opts = flatten(tree);
                setFolderOptions([{ id: null, label: '— None —' }, ...opts]);
            } catch (e) {
                console.error('Failed to load folders', e);
                setFolderOptions([{ id: null, label: '— None —' }]);
            }
        };
        loadFolders();
    }, [user?.id]);

    // -------- Select-from-bank logic --------
    const fetchPick = async (targetPage: number = pickPage) => {
        if (!user?.id) return;
        try {
            setPickLoading(true);
            const res = await questionApi.listForInstitutionPaged(user.id, {
                page: targetPage,
                size: pickSize,
                search: pickSearch || undefined,
                subject: pickSubject || undefined,
                classLevel: pickClass || undefined,
            });
            setPickList(res.content || []);
            setPickTotal(res.totalElements || 0);
            setPickTotalPages(res.totalPages || 0);
            setPickPage(res.page ?? targetPage);
        } catch (e) {
            console.error('Failed to load institution questions', e);
            setPickList([]);
            setPickTotal(0);
            setPickTotalPages(0);
        } finally {
            setPickLoading(false);
        }
    };

    // When modal opens or filters/page change, fetch
    useEffect(() => {
        if (!selectOpen) return;
        fetchPick(pickPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectOpen, user?.id, pickPage, pickSearch, pickSubject, pickClass]);

    const toggleSelect = (id: number, checked: boolean) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (checked) next.add(id); else next.delete(id);
            return next;
        });
    };

    const importSelected = async () => {
        // Map selected IDs to the actual question objects we have seen in pickList; in a simple implementation
        // we will import those from the currently loaded page that are selected. For selections across pages,
        // this will import any that are present on the current page and selected. For broader persistence,
        // a future improvement can keep a map of selected questions across pages.
        try {
            setImporting(true);
            const toImport = pickList.filter(q => q.id && selectedIds.has(q.id));
            if (toImport.length === 0) {
                setSelectOpen(false);
                setSelectedIds(new Set());
                return;
            }
            const cloned: Question[] = toImport.map((q) => ({
                id: undefined,
                questionText: q.questionText || '',
                questionRichText: q.questionRichText || '',
                shapesJson: q.shapesJson || '[]',
                imagePath: q.imagePath || '',
                imageAsPrimary: !!q.imageAsPrimary,
                subject: q.subject,
                classLevel: q.classLevel,
                complexity: q.complexity as any,
                tags: q.tags ? [...q.tags] : [],
                questionType: q.questionType,
                marks: q.marks,
                answers: (q.answers || []).map(a => ({ id: undefined, answerText: a.answerText, isCorrect: !!a.isCorrect })),
            }));
            setQuestions(prev => [...prev, ...cloned]);
            setSelectOpen(false);
            setSelectedIds(new Set());
        } catch (e) {
            console.error('Failed to import selected questions', e);
        } finally {
            setImporting(false);
        }
    };

    const handleCreateCategory = async () => {
        if (!newCategoryName.trim() || !user?.id) return;
        try {
            const created = await categoryApi.create(newCategoryName.trim(), user.id);
            const updated = await categoryApi.list();
            setCategories(updated);
            setCategoryId(created.id);
            setNewCategoryName('');
        } catch (e) {
            console.error('Failed to create subject', e);
            setError('Failed to create subject');
        }
    };

    // ----- Rich Editor Helpers: Tables for vertical/horizontal alignment -----
    const insertTableAtSelection = (rows: number, cols: number) => {
        rows = Math.max(1, Math.min(20, Math.floor(rows)));
        cols = Math.max(1, Math.min(20, Math.floor(cols)));
        const cellStyle = "min-width:28px;padding:6px 8px;text-align:right;vertical-align:middle;border:1px solid #ccc;line-height:1.3;min-height:1.6em;font-size:inherit;";
        const cells = Array.from({ length: rows })
            .map(() => `<tr>${Array.from({ length: cols }).map(() => `<td style=\"${cellStyle}\">&nbsp;</td>`).join('')}</tr>`)
            .join('');
        const html = `<table style=\"border-collapse:collapse;border:1px solid #999;margin:4px 0;\">${cells}</table>`;
        document.execCommand('insertHTML', false, html);
    };

    const handleInsertTable = (qIndex: number) => {
        const el = editorRefs.current[qIndex];
        if (!el) return;

        // Ensure focus and a caret inside the editor BEFORE prompting (so we know where to insert)
        el.focus();
        let sel = window.getSelection();
        if (!sel || sel.rangeCount === 0 || !el.contains(sel.anchorNode)) {
            const range = document.createRange();
            range.selectNodeContents(el);
            range.collapse(false); // place at end
            sel = window.getSelection();
            sel?.removeAllRanges();
            sel?.addRange(range);
        }

        // Ask for table size
        const colsStr = window.prompt('How many columns (digits)?', '3');
        if (colsStr == null) return;
        const rowsStr = window.prompt('How many rows?', '2');
        if (rowsStr == null) return;
        const cols = parseInt(colsStr, 10);
        const rows = parseInt(rowsStr, 10);
        if (Number.isNaN(cols) || Number.isNaN(rows)) return;

        // Prompts can steal focus/selection. Restore caret inside editor again.
        el.focus();
        sel = window.getSelection();
        if (!sel || sel.rangeCount === 0 || !el.contains(sel.anchorNode)) {
            const range = document.createRange();
            range.selectNodeContents(el);
            range.collapse(false);
            sel = window.getSelection();
            sel?.removeAllRanges();
            sel?.addRange(range);
        }

        // Insert the table at the caret
        insertTableAtSelection(rows, cols);

        // Move caret into the first cell of the last inserted table for immediate typing
        try {
            const tables = el.querySelectorAll('table');
            const lastTable = tables[tables.length - 1] as HTMLTableElement | undefined;
            const firstTd = lastTable?.querySelector('td') as HTMLElement | null | undefined;
            if (firstTd) {
                const r = document.createRange();
                r.selectNodeContents(firstTd);
                r.collapse(true);
                const s = window.getSelection();
                s?.removeAllRanges();
                s?.addRange(r);
            }
        } catch {}

        // Persist content now instead of waiting for blur
        updateQuestion(qIndex, 'questionRichText', el.innerHTML);
    };

    const handleToggleBorders = (qIndex: number) => {
        const el = editorRefs.current[qIndex];
        if (!el) return;
        const tables = Array.from(el.querySelectorAll('table')) as HTMLTableElement[];
        if (tables.length === 0) return;
        const anyVisible = tables.some(t => (t.style.borderStyle !== 'none' && t.style.border !== '0px' && t.getAttribute('data-borders') !== 'hidden'));
        const makeHidden = anyVisible; // if any visible, hide; if all hidden, show
        tables.forEach(t => {
            const cells = Array.from(t.querySelectorAll('th,td')) as HTMLElement[];
            if (makeHidden) {
                t.style.border = '0';
                t.style.borderCollapse = 'collapse';
                t.setAttribute('data-borders', 'hidden');
                cells.forEach(c => { c.style.border = '0'; });
            } else {
                t.style.border = '1px solid #999';
                t.style.borderCollapse = 'collapse';
                t.removeAttribute('data-borders');
                cells.forEach(c => { c.style.border = '1px solid #ccc'; });
            }
        });
        updateQuestion(qIndex, 'questionRichText', el.innerHTML);
    };

    // --- Shapes drag state & helpers ---
    type DragState = {
        qIndex: number;
        shapeIndex: number;
        type: string;
        startX: number;
        startY: number;
        orig: any;
        origPoints?: { x: number; y: number }[]; // for polygons
    } | null;

    const dragRef = useRef<DragState>(null);

    const svgEventToViewBox = (e: any) => {
        // Ensure we resolve the actual <svg> element even if the event originates from a child shape
        const ct = e.currentTarget as Element | null;
        const tg = e.target as Element | null;
        const svg: SVGSVGElement | null =
            (ct instanceof (window as any).SVGSVGElement ? (ct as SVGSVGElement) : (ct as any)?.ownerSVGElement) ||
            (tg instanceof (window as any).SVGSVGElement ? (tg as SVGSVGElement) : (tg as any)?.ownerSVGElement) ||
            (ct && (ct as any).closest ? ((ct as any).closest('svg') as SVGSVGElement) : null) ||
            (tg && (tg as any).closest ? ((tg as any).closest('svg') as SVGSVGElement) : null);

        if (!svg) {
            // Fallback safe values
            const clientX = e.clientX ?? (e.touches?.[0]?.clientX || 0);
            const clientY = e.clientY ?? (e.touches?.[0]?.clientY || 0);
            return { x: clientX, y: clientY };
        }

        const rect = svg.getBoundingClientRect();
        const vb = (svg as any).viewBox?.baseVal ?? { x: 0, y: 0, width: rect.width || 1, height: rect.height || 1 };
        const clientX = e.clientX ?? (e.touches?.[0]?.clientX || 0);
        const clientY = e.clientY ?? (e.touches?.[0]?.clientY || 0);
        const x = ((clientX - rect.left) / rect.width) * vb.width + vb.x;
        const y = ((clientY - rect.top) / rect.height) * vb.height + vb.y;
        return { x, y };
    };

    const startShapeDrag = (e: any, qIndex: number, shapeIndex: number, shape: any) => {
        e.preventDefault();
        e.stopPropagation();
        const { x, y } = svgEventToViewBox(e);
        let origPoints: { x: number; y: number }[] | undefined;
        if (shape.type === 'trapezium' && typeof shape.points === 'string') {
            origPoints = shape.points.split(/\s+/).map((p: string) => {
                const [px, py] = p.split(',').map((n: string) => parseFloat(n));
                return { x: px, y: py };
            });
        }
        dragRef.current = {
            qIndex,
            shapeIndex,
            type: shape.type,
            startX: x,
            startY: y,
            orig: { ...shape },
            origPoints,
        };
    };

    const handleShapeDragMove = (e: any, qIndex: number) => {
        if (!dragRef.current || dragRef.current.qIndex !== qIndex) return;
        const { x, y } = svgEventToViewBox(e);
        const dx = x - dragRef.current.startX;
        const dy = y - dragRef.current.startY;
        const shapes = JSON.parse(questions[qIndex].shapesJson || '[]');
        const idx = dragRef.current.shapeIndex;
        const s = { ...shapes[idx] };
        switch (dragRef.current.type) {
            case 'circle':
                s.cx = (dragRef.current.orig.cx || 0) + dx;
                s.cy = (dragRef.current.orig.cy || 0) + dy;
                break;
            case 'rect':
                s.x = (dragRef.current.orig.x || 0) + dx;
                s.y = (dragRef.current.orig.y || 0) + dy;
                break;
            case 'square':
                s.x = (dragRef.current.orig.x || 0) + dx;
                s.y = (dragRef.current.orig.y || 0) + dy;
                break;
            case 'trapezium':
                if (dragRef.current.origPoints) {
                    const moved = dragRef.current.origPoints.map(pt => ({ x: pt.x + dx, y: pt.y + dy }));
                    s.points = moved.map(pt => `${pt.x},${pt.y}`).join(' ');
                }
                break;
            default:
                break;
        }
        shapes[idx] = s;
        updateQuestion(qIndex, 'shapesJson', JSON.stringify(shapes));
    };

    const endShapeDrag = () => {
        dragRef.current = null;
    };

    const totalMarks = questions.reduce((sum, q) => sum + (q.marks || 0), 0);

    const addQuestion = (type: QuestionType) => {
        // Default question metadata from exam-level selections
        const subjectName = categories.find(c => c.id === (categoryId ?? -1))?.name;
        const newQuestion: Question = {
            questionText: '',
            questionRichText: '',
            shapesJson: '[]',
            imagePath: '',
            imageAsPrimary: false,
            // Default metadata
            subject: subjectName, // default from selected exam subject (category)
            classLevel: classLevel, // default from selected exam class
            // Leave complexity and tags undefined (optional per question)
            questionType: type,
            marks: 5,
            answers: type === 'MCQ'
                ? [
                    { answerText: '', isCorrect: true },
                    { answerText: '', isCorrect: false },
                    { answerText: '', isCorrect: false },
                    { answerText: '', isCorrect: false },
                ]
                : [],
        };
        setQuestions([...questions, newQuestion]);
    };

    const updateQuestion = (index: number, field: string, value: string | number | boolean) => {
        const updated = [...questions];
        updated[index] = { ...updated[index], [field]: value };
        setQuestions(updated);
    };

    const updateAnswer = (qIndex: number, aIndex: number, field: string, value: string | boolean) => {
        const updated = [...questions];
        const answers = [...(updated[qIndex].answers || [])];

        if (field === 'isCorrect' && value === true) {
            // Only one correct answer allowed for MCQs
            answers.forEach((a, i) => {
                answers[i] = { ...a, isCorrect: i === aIndex };
            });
        } else {
            answers[aIndex] = { ...answers[aIndex], [field]: value };
        }

        updated[qIndex] = { ...updated[qIndex], answers };
        setQuestions(updated);
    };

    const removeQuestion = (index: number) => {
        setQuestions(questions.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (questions.length === 0) {
            setError('Please add at least one question');
            return;
        }

        // Validate each question has either plain/rich text OR a primary image
        for (let i = 0; i < questions.length; i++) {
            const q = questions[i];
            const plain = (q.questionText || '').trim();
            const rich = (q.questionRichText || '')
                .replace(/<(script|style)[^>]*>[\s\S]*?<\/(script|style)>/gi, ' ')
                .replace(/<[^>]+>/g, ' ')
                .replace(/&nbsp;/g, ' ')
                .replace(/\s+/g, ' ')
                .trim();
            const hasPrimaryImage = !!q.imagePath && q.imagePath.trim().length > 0 && !!q.imageAsPrimary;
            if (!plain && !rich && !hasPrimaryImage) {
                setError(`Question ${i + 1}: Provide text/rich text or mark an uploaded image as primary.`);
                return;
            }
        }

        // Validate MCQ questions have 4 answers with one
        for (let i = 0; i < questions.length; i++) {
            const q = questions[i];
            if (q.questionType === 'MCQ') {
                if (!q.answers || q.answers.length < 4) {
                    setError(`Question ${i + 1}: MCQ must have at least 4 answers`);
                    return;
                }
                const correctCount = q.answers.filter((a: Answer) => a.isCorrect).length;
                if (correctCount !== 1) {
                    setError(`Question ${i + 1}: MCQ must have exactly one correct answer`);
                    return;
                }
            }
        }

        setSaving(true);
        try {
            if (!categoryId) {
                setError('Please select a subject for this exam.');
                setSaving(false);
                return;
            }
            await examApi.create(
                {
                    title,
                    description,
                    durationMinutes,
                    totalMarks,
                    isActive: true,
                    categoryId,
                    classLevel,
                    folderId: selectedFolderId ?? undefined,
                    questions,
                },
                user!.id
            );
            navigate('/');
        } catch (err) {
            setError('Failed to create exam, Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const handleSaveDraft = async () => {
        setError('');
        if (!title.trim()) {
            setError('Please provide a title to save a draft.');
            return;
        }
        setSaving(true);
        try {
            const saved = await examApi.createDraft(
                {
                    title,
                    description,
                    durationMinutes, // optional
                    totalMarks, // optional
                    // category is optional for draft now; backend will use a placeholder if not provided
                    categoryId,
                    classLevel,
                    folderId: selectedFolderId ?? undefined,
                    questions, // can be empty or partial
                },
                user!.id
            );
            // Navigate to Edit Exam to continue
            navigate(`/edit-exam/${saved.id}`);
        } catch (err) {
            console.error('Failed to save draft', err);
            setError('Failed to save draft. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const handleSaveDraftAndClose = async () => {
        setError('');
        if (!title.trim()) {
            setError('Please provide a title to save a draft.');
            return;
        }
        setSaving(true);
        try {
            await examApi.createDraft(
                {
                    title,
                    description,
                    durationMinutes,
                    totalMarks,
                    categoryId,
                    classLevel,
                    questions,
                },
                user!.id
            );
            // After saving, close the page (go back to home/dashboard)
            navigate('/');
        } catch (err) {
            console.error('Failed to save draft', err);
            setError('Failed to save draft. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div data-testid="create-exam">
            <div className="card" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                <h1 style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>
                    ✨ Create New Exam
                </h1>
                <p style={{ opacity: 0.9 }}>
                    Build an amazing exam for your students!
                </p>
            </div>

            {error && <div className="alert alert-error">{error}</div>}

            <form onSubmit={handleSubmit}>
                <div className="card">
                    <h3 style={{ marginBottom: '1.5rem' }}>📋 Exam Details</h3>

                    <div className="form-group">
                        <label className="form-label" htmlFor="title">
                            📚 Exam Title
                        </label>
                        <input
                            type="text"
                            id="title"
                            className="form-input"
                            placeholder="e.g., Fun Math Quiz 🧮"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            required
                            data-testid="exam-title"
                        />
                    </div>

                    <div className="form-group">
                        <label className="form-label" htmlFor="description">
                            📝 Description
                        </label>
                        <textarea
                            id="description"
                            className="form-input"
                            placeholder="Describe what this exam is about..."
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            data-testid="exam-description"
                        />
                    </div>

                    <div className="grid grid-2">
                        <div className="form-group">
                            <label className="form-label" htmlFor="folder">
                                🗂 Folder
                            </label>
                            <select
                                id="folder"
                                className="form-input"
                                value={String(selectedFolderId)}
                                onChange={(e) => setSelectedFolderId(e.target.value === 'null' ? null : Number(e.target.value))}
                            >
                                {folderOptions.map(opt => (
                                    <option key={String(opt.id)} value={String(opt.id)}>{opt.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="form-group">
                            <label className="form-label" htmlFor="duration">
                                ⏱️ Duration (minutes)
                            </label>
                            <input
                                type="number"
                                id="duration"
                                className="form-input"
                                min="1"
                                value={durationMinutes}
                                onChange={(e) => setDurationMinutes(Number(e.target.value))}
                                required
                                data-testid="exam-duration"
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label className="form-label" htmlFor="category">
                            📘 Subject
                        </label>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                            <select
                                id="category"
                                className="form-input"
                                value={categoryId ?? ''}
                                onChange={(e) => setCategoryId(e.target.value ? Number(e.target.value) : undefined)}
                                required
                                data-testid="exam-category"
                            >
                                <option value="" disabled>Select subject</option>
                                {categories.map(c => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </select>
                            <input
                                type="text"
                                placeholder="New subject name"
                                className="form-input"
                                value={newCategoryName}
                                onChange={(e) => setNewCategoryName(e.target.value)}
                                style={{ maxWidth: '220px' }}
                            />
                            <button type="button" className="btn btn-outline" onClick={handleCreateCategory} disabled={!newCategoryName.trim() || saving}>
                                ➕ Add
                            </button>
                        </div>
                        <div style={{ fontSize: '0.85rem', color: '#718096', marginTop: '0.25rem' }}>
                            Can't find a subject? Type a name and click Add.
                        </div>
                    </div>

                    {/* Class/Grade */}
                    <div className="form-group">
                        <label className="form-label" htmlFor="classLevel">
                            🎓 Class
                        </label>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                            <select
                                id="classLevel"
                                className="form-input"
                                value={classLevel}
                                onChange={(e) => setClassLevel(e.target.value)}
                                data-testid="exam-class"
                            >
                                {classes.map((cl) => (
                                    <option key={cl} value={cl}>{cl}</option>
                                ))}
                            </select>
                            <input
                                type="text"
                                placeholder="Add new class (e.g., 3rd Class)"
                                className="form-input"
                                value={newClassName}
                                onChange={(e) => setNewClassName(e.target.value)}
                                style={{ maxWidth: '220px' }}
                            />
                            <button
                                type="button"
                                className="btn btn-outline"
                                disabled={!newClassName.trim()}
                                onClick={() => {
                                    const name = newClassName.trim();
                                    if (!name) return;
                                    if (!classes.includes(name)) setClasses([...classes, name]);
                                    setClassLevel(name);
                                    setNewClassName('');
                                }}
                            >➕ Add</button>
                        </div>
                        <div style={{ fontSize: '0.85rem', color: '#718096', marginTop: '0.25rem' }}>
                            Default is "Open" if the exam doesn't belong to any class.
                        </div>
                    </div>


                    <div style={{ padding: '1rem', backgroundColor: '#f0f4f8', borderRadius: '15px' }}>
                        <strong>📊 Total Marks: {totalMarks}</strong>
                    </div>
                </div>

                {/* Questions */}
                <h3 style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    ❓ Questions ({questions.length})
                </h3>

                {questions.map((question, qIndex) => (
                    <div
                        key={qIndex}
                        className={`question-card ${question.questionType.toLowerCase()}`}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                            <div>
                                <span className="question-number">{qIndex + 1}</span>
                                <span className="badge badge-info" style={{ marginLeft: '0.5rem' }}>
                                    {question.questionType === 'MCQ' ? '🔘 MCQ' : '✍ Subjective'}
                                </span>
                            </div>
                            <button
                                type="button"
                                className="btn btn-danger btn-sm"
                                onClick={() => removeQuestion(qIndex)}
                            >
                                🗑️ Remove
                            </button>
                        </div>

                        {/* Shared Question Editor */}
                        {(() => {
                            const subjectName = categories.find(c => c.id === (categoryId ?? -1))?.name;
                            return (
                                <QuestionEditor
                                    question={question}
                                    onChange={(patch) => {
                                        const updated = { ...question, ...patch };
                                        const list = [...questions];
                                        list[qIndex] = updated;
                                        setQuestions(list);
                                    }}
                                    showSubjectClass={false}
                                    showComplexityTags={true}
                                    categories={categories}
                                    classes={classes}
                                    defaultSubject={subjectName}
                                    defaultClassLevel={classLevel}
                                />
                            );
                        })()}
                    </div>
                ))}

                {/* Add Question Buttons */}
                <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => addQuestion('MCQ')}
                        data-testid="add-mcq"
                    >
                        ➕ Add MCQ
                    </button>
                    <button
                        type="button"
                        className="btn btn-outline"
                        onClick={() => addQuestion('SUBJECTIVE')}
                        data-testid="add-subjective"
                    >
                        ➕ Add Subjective
                    </button>
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setSelectOpen(true)}
                        data-testid="add-from-bank"
                    >
                        📚 Add from Bank
                    </button>
                </div>

                {/* Select From Bank Modal */}
                {selectOpen && (
                    <div className="modal" style={{ display: 'block', width: '95vw', maxWidth: '100%', maxHeight: 'none', overflowY: 'visible' }}>
                        <div className="modal-content" style={{ width: '95vw', maxWidth: '100%', maxHeight: 'none', overflowY: 'visible' }}>
                            <div className="modal-header">
                                <h3>Select Questions from {user?.role === 'SUPER_ADMIN' ? 'All Institutions' : 'Your Institution'}</h3>
                                <button className="close" onClick={() => setSelectOpen(false)}>×</button>
                            </div>
                            <div className="modal-body" style={{ maxHeight: 'none', overflowY: 'visible' }}>
                                {/* Filters */}
                                <div className="card" style={{ marginBottom: '0.75rem' }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 220px 180px auto', gap: '0.5rem' }}>
                                        <input
                                            type="text"
                                            className="form-input"
                                            placeholder="Search text or tag"
                                            value={pickSearch}
                                            onChange={(e) => { setPickSearch(e.target.value); setPickPage(0); }}
                                        />
                                        <input
                                            type="text"
                                            className="form-input"
                                            placeholder="Subject"
                                            value={pickSubject}
                                            onChange={(e) => { setPickSubject(e.target.value); setPickPage(0); }}
                                        />
                                        <input
                                            type="text"
                                            className="form-input"
                                            placeholder="Class"
                                            value={pickClass}
                                            onChange={(e) => { setPickClass(e.target.value); setPickPage(0); }}
                                        />
                                        <div>
                                            <button type="button" className="btn btn-outline" onClick={() => { setPickSearch(''); setPickSubject(''); setPickClass(''); setPickPage(0); }}>Reset</button>
                                        </div>
                                    </div>
                                </div>

                                {/* Table */}
                                <div className="table-container" style={{ overflowY: 'visible', maxHeight: 'none' }}>
                                    <table className="table">
                                        <thead>
                                            <tr>
                                                <th></th>
                                                <th>Subject</th>
                                                <th>Class</th>
                                                <th>Type</th>
                                                <th>Marks</th>
                                                <th>Tags</th>
                                                <th>Preview</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {pickLoading ? (
                                                <tr><td colSpan={7}><div className="spinner" /></td></tr>
                                            ) : pickList.length === 0 ? (
                                                <tr><td colSpan={7}>No questions found</td></tr>
                                            ) : pickList.map((q) => (
                                                <tr key={q.id}>
                                                    <td>
                                                        <input type="checkbox" checked={!!selectedIds.has(q.id!)} onChange={(e) => toggleSelect(q.id!, e.target.checked)} />
                                                    </td>
                                                    <td>{q.subject || '-'}</td>
                                                    <td>{q.classLevel || '-'}</td>
                                                    <td>{q.questionType}</td>
                                                    <td>{q.marks}</td>
                                                    <td>{(q.tags || []).slice(0,3).join(', ')}{(q.tags||[]).length>3?` +${(q.tags||[]).length-3}`:''}</td>
                                                    <td style={{ maxWidth: 320 }}>
                                                        {q.imageAsPrimary && q.imagePath ? (
                                                            <img src={q.imagePath} alt="q" style={{ maxHeight: 60 }} />
                                                        ) : (
                                                            <span title={(q.questionText || '').substring(0, 200)}>
                                                                {(q.questionText || '').substring(0, 60)}
                                                            </span>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>

                                    {/* Pagination */}
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                                        <div>Showing {pickList.length} of {pickTotal} results</div>
                                        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                            <button className="btn btn-outline btn-sm" disabled={pickPage<=0} onClick={() => setPickPage(p => Math.max(0, p-1))}>Prev</button>
                                            {Array.from({ length: pickTotalPages }).map((_, i) => (
                                                <button key={i} className={"btn btn-sm "+(i===pickPage? 'btn-primary':'btn-outline')} disabled={i===pickPage} onClick={() => setPickPage(i)}>{i+1}</button>
                                            ))}
                                            <button className="btn btn-outline btn-sm" disabled={pickPage>=pickTotalPages-1} onClick={() => setPickPage(p => Math.min(pickTotalPages-1, p+1))}>Next</button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn" onClick={() => setSelectOpen(false)}>Cancel</button>
                                <button className="btn btn-primary" disabled={selectedIds.size===0 || importing} onClick={importSelected}>{importing? 'Adding...' : `Add ${selectedIds.size} Selected`}</button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Submit / Draft Buttons */}
                <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                    <button
                        type="button"
                        className="btn btn-outline btn-lg"
                        disabled={saving}
                        onClick={() => navigate('/')}
                        data-testid="exit"
                    >
                        🚪 Exit
                    </button>
                    <button
                        type="button"
                        className="btn btn-outline btn-lg"
                        disabled={saving}
                        onClick={handleSaveDraft}
                        data-testid="save-draft"
                    >
                        💾 Save as Draft
                    </button>
                    <button
                        type="button"
                        className="btn btn-secondary btn-lg"
                        disabled={saving}
                        onClick={handleSaveDraftAndClose}
                        data-testid="save-draft-close"
                    >
                        ✅ Save and Close
                    </button>
                    <button
                        type="submit"
                        className="btn btn-success btn-lg"
                        disabled={saving}
                        data-testid="submit-exam"
                    >
                        {saving ? '⌛ Creating...' : '✨ Create Exam'}
                    </button>
                </div>
            </form>
        </div>
    );
}

export default CreateExam;
